import template from 'lodash/template';
import { marked } from 'marked';
import PropTypes from 'prop-types';
import React from 'react';
import slugify from 'slugify';

const config = {};

export function getMeasurementDataForInterpolation(feedbackResponse) {
  const measurementScores = feedbackResponse?.measurement_scores || {};
  return {
    measurement_nps: measurementScores.nps,
    measurement_csat: measurementScores.csat,
    measurement_ces: measurementScores.ces,
    measurement_emotional_score: measurementScores.emotional_score,
    measurement_weighted_score: measurementScores.weighted_score,
    measurement_interview_score: measurementScores.interview_score,
  };
}

export const configureTranslate = (options) => {
  if (options.parseEmojiFn) {
    config.parseEmojiFn = options.parseEmojiFn;
  }

  if (options.translations) {
    config.translations = Object.assign(config.translations || {}, options.translations);
  }
};

/**
  Examples:

  <Translate msg="genericValidationError" />
  Output: <span>Your answer could not be validated ...</span>
  Gets the 'genericValidationError' from 'translations.cson', translates it to
  the current language (gets it from Redux) and returns it wrapped in a span (default).

  If you want to wrap it with another component:
  <Translate component="div" msg="genericValidationError" />
  Output: <div>Your answer could not be validated ...</div>

  Component can be anything, even a React component:
  <Translate component={MyComponent} msg="genericValidationError" />
  Output: <MyComponent>Your answer could not be validated ...</MyComponent>

  If you want to give translated props to the wrapper, give them in `attributes`:
  <Translate msg="genericValidationError" attributes={{ key: 1 }} />
  Output: <span key={1}>Your answer could not be validated ...</span>

  Given props will be automatically translated:
  <Translate msg="genericValidationError" attributes={{ title: "notFound" }} />
  Output: <span title="404 Not Found">Your answer could not be validated ...</span>

  If you translation contains parameters (e.g. "Error ${errorCode}") give them
  in messageProps:
  <Translate msg="error" errorCode={123} />
  Output: <span>Error 123</span>

  You can inline your message object:
  <Translate msg={{ en: "wow", tr: "woow" }} />
  Output: <span>wow</span>

  You can enable markdown parsing with 'parseMarkdown':
  <Translate parseMarkdown msg={{ en: "**Amazing**!", tr: "**Müthiş**" }} />
  Output: <span><strong>Amazing</strong>!</span>

  You can put HTML in your translations (dangerous):
  <Translate dangerousHtml msg={{ en: "<i>No<i>", tr: "<i>Hayır</i>" }} />
  Output: <span><i>No</i></span>
 */

/**
 * Applies params to the translation key.
 * Example: Transforms 'Hello ${name}!' to 'Hello Samed!' assuming `params` is
 * equal to `{ name: 'Samed' }`.
 */
const applyParams = (translation, params) => {
  let replaced = translation;
  Object.keys(params).forEach((param) => {
    replaced = replaced.split(`\${${param}}`).join(params[param]);
  });
  return replaced;
};

/** Returns the translated message in the given language with the given params. */
export const t = (message, languageCode, params, isNullableMessage) => {
  // Get the object that contains multiple translations with language codes as keys.
  let messageObject;
  if (typeof message === 'object') {
    messageObject = message;
  } else {
    messageObject = config.translations[message];
    if (!messageObject) {
      return message;
    }
  }

  // Get the translation.
  let translation = messageObject[languageCode.toLowerCase()] || messageObject[languageCode.toUpperCase()];

  // If element has nullable translation option on selected language, skip this part and return "" translation
  // e.g. NPS empty description
  if (!isNullableMessage) {
    // If no translation, get the one in the browser language.
    /* istanbul ignore if */
    if (!translation) {
      const browserLanguage = (navigator.language || navigator.userLanguage || '').split('-')[0];
      if (browserLanguage) {
        translation = messageObject[browserLanguage.toLowerCase()] || messageObject[browserLanguage.toUpperCase()];
      }
    }

    // If still no translation, get the first translation.
    if (!translation) {
      // It must be English language, otherwise Object.values(messageObject)[0] will do.
      translation = messageObject?.en || Object.values(messageObject)[0];
    }
  }

  // Apply the parameters, e.g. 'Hello ${name}!' => 'Hello Samed!' if name is
  // given in the props.
  return applyParams(translation, params || {});
};

// This function helps to return translation as string.
// for now it used only for dropdown with interpolateCreator. you can extend it for other cases.
export function DisplayAsString(props) {
  const {
    languageCode,
    message,
    messageProps,
    interpolateCreator,
    creator,
    responses,
    feedbackResponse,
    isNullableMessage,
  } = props;

  let translation = t(message, languageCode, messageProps, isNullableMessage) || '';

  if (interpolateCreator) {
    try {
      const compiled = template(translation, { interpolate: /{{([\s\S]+?)}}/g });
      const measurementData = getMeasurementDataForInterpolation(feedbackResponse);
      translation = compiled({ ...feedbackResponse, ...creator, ...responses, ...measurementData });
    } catch (e) {
      console.error('Error interpolating creator:', e);
      translation = translation && translation.toString() && translation.toString().replace(/{{([\s\S]+?)}}/g, '');
    }
  }

  return translation;
}

/**
 * Simple translation function for use in withTranslation HOC (returns string, not React node).
 */
export function translate(message, params = {}, languageCode = 'EN', isNullableMessage = false) {
  return t(message, languageCode, params, isNullableMessage) || '';
}

export default function Translate(inputProps) {
  const {
    languageCode = 'EN',
    message,
    messageProps,
    component = 'span',
    dangerousHtml,
    attributes = {},
    parseMarkdown,
    markdownOptions,
    sanitizeMarkdown,
    emojify,
    interpolateCreator,
    creator,
    interpolateNodeId,
    nodeId,
    responses,
    bindingData,
    feedbackResponse,
    isNullableMessage = false,
    elementRef,
    ...restProps
  } = inputProps;
  const elementProps = restProps;

  if (elementRef) {
    elementProps.ref = elementRef;
  }

  // Translate the element properties.
  Object.keys(attributes).forEach((attr) => {
    elementProps[attr] = t(attributes[attr], languageCode, restProps);
  });

  // If there is no message, just translate the attributes.
  if (!message) {
    return React.createElement(component, elementProps);
  }

  let translation = t(message, languageCode, messageProps, isNullableMessage) || '';

  if (emojify && config.parseEmojiFn) {
    translation = config.parseEmojiFn(translation);
  }

  if (parseMarkdown) {
    const renderer = new marked.Renderer();

    renderer.heading = function ({ depth, tokens }) {
      const headingContent = this.parser.parseInline(tokens);
      const inlineText = this.parser.parseInline(tokens, new marked.TextRenderer());
      const replaced = inlineText
        .toLowerCase()
        .trim()
        .replace(/<[!/a-z].*?>/gi, ''); // Replace HTML tags in the text
      return `<h${depth} id="${slugify(replaced)}">${headingContent}</h${depth}>\n`;
    };

    if (bindingData) {
      try {
        const bindingCompiled = template(translation, { interpolate: /{{([^{}]*\.[^{}]*)}}/g });
        const measurementData = getMeasurementDataForInterpolation(feedbackResponse);
        translation = bindingCompiled({ ...bindingData, ...measurementData });
      } catch (e) {
        console.log('binding error', e);
        translation =
          translation && translation.toString() && translation.toString().replace(/{{([^{}]*\.[^{}]*)}}/g, '');
      }
    }

    if (markdownOptions?.link) {
      const linkRenderer = renderer.link;
      renderer.link = (token) => {
        const { href: tokenHref } = token;
        let href = tokenHref;

        // Check if href is a string (backward compatibility) or an object (new format)
        if (typeof href === 'object' && href.href) {
          href = href.href;
        }

        const localLink = href.startsWith(`${window.location.protocol}//${window.location.hostname}`);
        const compiled = template(href, { interpolate: /{{([\s\S]+?)}}/g });
        const measurementData = getMeasurementDataForInterpolation(feedbackResponse);
        href = compiled({ ...feedbackResponse, ...creator, ...responses, ...measurementData });
        const html = linkRenderer.call(renderer, token);
        if (localLink) {
          return html;
        }
        let attrs = '';
        for (const [key, value] of Object.entries(markdownOptions?.link)) {
          attrs += `${key}= "${value}" `;
        }
        return html.replace(/^<a href=/, `<a ${attrs} href=`);
      };
    }

    translation = marked(translation, { sanitize: !!sanitizeMarkdown, renderer }).replace(/<p>(.*)<\/p>/i, '$1');
  }

  if (interpolateNodeId) {
    try {
      const compiled = template(translation, { interpolate: /{{([^.]+?)}}/g });
      const measurementData = getMeasurementDataForInterpolation(feedbackResponse);
      translation = compiled({ nodeId, ...feedbackResponse, ...responses, ...measurementData });
    } catch (e) {
      console.error('Error interpolating nodeId:', e);

      // This creates a constraint where you cannot use nodeId and creator interpolation
      // at the same time. Luckily, we don't have any customers who use creator
      // interpolation.
      translation = translation && translation.toString() && translation.toString().replace(/{{([\s\S]+?)}}/g, '');
    }
  }

  if (interpolateCreator) {
    try {
      const compiled = template(translation, { interpolate: /{{([\s\S]+?)}}/g });
      translation = compiled({ ...feedbackResponse, ...creator, ...responses });
    } catch (e) {
      console.error('Error interpolating creator:', e);

      // remove the {{ bla bla }} stuff
      translation = translation && translation.toString() && translation.toString().replace(/{{([\s\S]+?)}}/g, '');
    }
  }

  if (dangerousHtml || parseMarkdown || emojify) {
    elementProps.dangerouslySetInnerHTML = { __html: translation };
    return React.createElement(component, elementProps);
  }
  return React.createElement(component, elementProps, translation);
}

Translate.propTypes = {
  languageCode: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  messageProps: PropTypes.object,
  parseMarkdown: PropTypes.bool,
  sanitizeMarkdown: PropTypes.bool,
  markdownOptions: PropTypes.object,
  emojify: PropTypes.bool,
  attributes: PropTypes.object,
  component: PropTypes.string,
  dangerousHtml: PropTypes.bool,
  interpolateCreator: PropTypes.bool,
  creator: PropTypes.object,
  interpolateNodeId: PropTypes.bool,
  bindingData: PropTypes.any,
  nodeId: PropTypes.string,
  responses: PropTypes.any,
  feedbackResponse: PropTypes.any,
  isNullableMessage: PropTypes.bool,
  elementRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};
